import { useCallback, useEffect, useState } from 'react';
import type { Eu } from '../../acesso';
import { ErroDeArea } from '../../design/Estados';
import { diaCurtoEmBrasilia } from '../../design/datas';
import { Icone } from '../../design/Icone';
import { useValoresOcultos } from '../../design/sigilo';
import {
  diaCurto,
  lerMinhaPremiacao,
  mesPorExtenso,
  MOTIVO_DO_CORTE,
  reais,
  semanaCurta,
} from '../../premiacao';
import type { MinhaPremiacao as Dados, Programa } from '../../premiacao';
import { TopoMovel } from '../../shell/ShellBalconista';
import { Comprovante } from './Premiacao';
import './minha.css';

/**
 * TELA 10 (parte A) — MINHA PREMIAÇÃO. "Reconhecimento adulto: número claro, origem explicada."
 *
 * O valor é o cheque DA PESSOA calculado no servidor (paridade com o 1.0): o preço real do HOS,
 * as canceladas e devoluções descontadas, a meta da semana. Esconder dedução é o que gera
 * desconfiança — os itens deduzidos ficam à vista, e a meta também: abaixo dela o valor não é pago.
 */

const ROTULO: Record<Programa, string> = {
  vitamina: 'Vitaminas',
  pre_vencido: 'Pré-vencidos',
  paradao: 'Paradão',
};

export function MinhaPremiacao({ eu }: { eu: Eu | null }) {
  // "Só números" (08/10): o servidor manda todo R$ do cheque nulo; aqui some o que seria dinheiro
  const ocultos = useValoresOcultos();
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState(false);
  const [todos, setTodos] = useState(false);
  const [comprovante, setComprovante] = useState(false);

  const carregar = useCallback(() => {
    setErro(false);
    void lerMinhaPremiacao().then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, []);
  useEffect(carregar, [carregar]);

  const c = d?.cheque ?? null;
  // Enquanto carrega, nada de "R$ 0,00 · Em apuração · 0 itens": o número falso parece real.
  const status = !d
    ? '—'
    : d.pagamento
      ? `Pago ${diaCurtoEmBrasilia(d.pagamento.pagoEm)}`
      : c && !c.metaBatida
        ? 'Abaixo da meta'
        : 'Em apuração';
  const programas = (['vitamina', 'pre_vencido', 'paradao'] as const).map((p) => ({
    p,
    itens: c ? c.linhas.bate.filter((l) => l.programa === p).reduce((s, l) => s + l.qtd, 0) : 0,
    valor: c ? c.tipos[p].bruto : 0,
  }));
  const canceladas = c?.linhas.canceladas.filter((l) => ocultos || l.desconto > 0).length ?? 0;
  const devolucoes = c?.linhas.devolvidos.length ?? 0;
  const naoConfirmados = c?.linhas.naoVendeu.reduce((s, l) => s + l.qtd, 0) ?? 0;
  const lista = d ? (todos ? d.pagamentos : d.pagamentos.slice(0, 3)) : [];
  // O que ficou de fora pelas regras — mostrar ensina a não perder de novo
  const perdidos = c
    ? [
        ...c.linhas.naoLancou.map((l) => ({
          regra: 'Vendeu e não lançou',
          nome: l.produto.nome,
          dia: l.dia,
          qtd: l.qtd,
          valor: l.perdeu,
        })),
        ...c.linhas.diaErrado.map((l) => ({
          regra: `Lançou em ${diaCurto(l.lancouEm)}, vendeu em ${diaCurto(l.dia)}`,
          nome: l.produto.nome,
          dia: l.dia,
          qtd: l.qtd,
          valor: l.perdeu,
        })),
        ...(c.linhas.loteDoMesmoDia ?? []).map((l) => ({
          regra: 'Lote vendido no dia em que entrou',
          nome: l.produto.nome,
          dia: l.dia,
          qtd: l.qtd,
          valor: l.perdeu,
        })),
      ].sort((a, b) => a.dia.localeCompare(b.dia))
    : [];
  const totalPerdido = perdidos.reduce((s, x) => s + x.valor, 0);

  return (
    <>
      <TopoMovel eu={eu} voltar="#/inicio" titulo="Minha premiação" />
      <div className="mp">
        <h1 className="mp-titulo-pc">Minha premiação</h1>
        {erro ? (
          <ErroDeArea oQue="sua premiação" aoTentar={carregar} />
        ) : (
          <div className="mp-grade">
            <div className="mp-coluna">
              <section className="mp-destaque" aria-busy={d === null}>
                <div className="mp-destaque-topo">
                  <div className="mp-destaque-linha">
                    <span>PREMIAÇÃO DESTA SEMANA</span>
                    <span className="mp-selo">{status}</span>
                  </div>
                  {ocultos ? (
                    c && (
                      <>
                        <div className="mp-valor">
                          {c.itensLiquidos} de {c.meta} itens
                        </div>
                        <div className="mp-semana">Valores ocultos pela gestão.</div>
                      </>
                    )
                  ) : (
                    <div className="mp-valor">{d ? reais(c?.aReceber ?? 0) : '—'}</div>
                  )}
                  <div className="mp-semana">
                    {d
                      ? `Semana ${semanaCurta(d.semana.de, d.semana.ate)} · fecha sexta às 23:59`
                      : ' '}
                  </div>
                </div>
                <div className="mp-origem">
                  <div className="mp-rotulo">DE ONDE VEIO</div>
                  {programas.map(({ p, itens, valor }) => (
                    <div key={p} className="mp-origem-linha">
                      <span>
                        {ROTULO[p]}
                        {d ? ` · ${String(itens)} ${itens === 1 ? 'item' : 'itens'}` : ''}
                      </span>
                      {!ocultos && <strong>{d ? reais(valor) : '—'}</strong>}
                    </div>
                  ))}
                </div>
              </section>

              {(c?.cortes ?? []).map((k) => (
                <section key={k.id} className="mp-meta mp-corte" role="status">
                  <Icone nome="warn" tamanho={16} traco={1.7} />
                  <div>
                    <strong>Sua vitamina de {mesPorExtenso(k.mes)} foi cortada</strong>
                    <p>
                      Motivo: {MOTIVO_DO_CORTE[k.tipo]}. {k.detalhe}. A vitamina desse mês não é
                      paga
                      {!ocultos && k.descontoDeSemanaPaga > 0
                        ? ` e a que já tinha sido paga (${reais(k.descontoDeSemanaPaga)}) é descontada agora`
                        : ''}
                      . Pré-vencido e paradão continuam valendo. Dúvida? Fale com o administrador.
                    </p>
                  </div>
                </section>
              ))}

              {c && !c.metaBatida && c.meta > 0 && (
                <section className="mp-meta" role="status">
                  <Icone nome="warn" tamanho={16} traco={1.7} />
                  <div>
                    <strong>
                      Meta da semana: {c.itensLiquidos} de {c.meta} itens confirmados
                    </strong>
                    <p>
                      Faltam {c.faltouParaMeta} {c.faltouParaMeta === 1 ? 'item' : 'itens'}. Abaixo
                      da meta a premiação da semana não é paga — o que você vendeu continua contando
                      até quinta.
                    </p>
                  </div>
                </section>
              )}

              {c &&
                (ocultos
                  ? canceladas > 0 || devolucoes > 0 || naoConfirmados > 0
                  : c.descontado > 0 || naoConfirmados > 0) && (
                  <section className="mp-deduzido">
                    <div className="mp-deduzido-topo">
                      <Icone nome="warn" tamanho={16} traco={1.7} />
                      <span>Itens deduzidos</span>
                      {!ocultos && (
                        <strong>{c.descontado > 0 ? `−${reais(c.descontado)}` : reais(0)}</strong>
                      )}
                    </div>
                    <p>
                      {[
                        canceladas > 0 &&
                          `${String(canceladas)} ${canceladas === 1 ? 'venda cancelada pelo administrador' : 'vendas canceladas pelo administrador'}`,
                        devolucoes > 0 &&
                          `${String(devolucoes)} ${devolucoes === 1 ? 'devolução' : 'devoluções'}`,
                        naoConfirmados > 0 &&
                          `${String(naoConfirmados)} ${naoConfirmados === 1 ? 'item ainda não confirmado' : 'itens ainda não confirmados'} na venda do HOS`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      .{' '}
                      {ocultos
                        ? ''
                        : c.descontado > 0
                          ? 'Já descontados do valor acima.'
                          : 'Não entram até o HOS confirmar.'}
                    </p>
                  </section>
                )}
              {perdidos.length > 0 && (
                <section className="mp-perdido">
                  <div className="mp-deduzido-topo">
                    <Icone nome="warn" tamanho={16} traco={1.7} />
                    <span>Ficou de fora pelas regras</span>
                    {!ocultos && <strong>{reais(totalPerdido)}</strong>}
                  </div>
                  <ul>
                    {perdidos.map((x, i) => (
                      <li key={`${x.dia}${x.nome}${String(i)}`}>
                        <div>
                          <b>{x.nome}</b>
                          <span>
                            {diaCurto(x.dia)} · {x.qtd} un · {x.regra}
                          </span>
                        </div>
                        {!ocultos && <em>{x.valor > 0 ? reais(x.valor) : '—'}</em>}
                      </li>
                    ))}
                  </ul>
                  <p>Lance no mesmo dia da venda, antes de passar no caixa, para não perder.</p>
                </section>
              )}
            </div>

            {!ocultos && (
              <div className="mp-coluna">
                <section className="mp-recebido">
                  <div className="mp-recebido-topo">
                    <h2>Já recebido</h2>
                    <span>
                      {d
                        ? `${String(d.pagamentos.length)} ${d.pagamentos.length === 1 ? 'pagamento' : 'pagamentos'}`
                        : ''}
                    </span>
                  </div>
                  <div className="mp-recebido-valor">{d ? reais(Number(d.jaRecebido)) : '—'}</div>
                  {lista.length > 0 && (
                    <div className="mp-recebido-lista">
                      {lista.map((p) => (
                        <div key={p.id}>
                          <span>
                            {diaCurtoEmBrasilia(p.pagoEm)} · semana{' '}
                            {semanaCurta(p.periodoDe, p.periodoAte)}
                          </span>
                          <strong>{reais(Number(p.valor))}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  {d && d.pagamentos.length > 3 && (
                    <button type="button" className="mp-botao" onClick={() => setTodos((v) => !v)}>
                      {todos ? 'Ver só os últimos' : 'Ver todos os pagamentos'}
                    </button>
                  )}
                  {d && d.pagamentos.length === 0 && (
                    <p className="mp-nada">Nenhum pagamento registrado ainda.</p>
                  )}
                </section>
                <button
                  type="button"
                  className="mp-botao mp-botao-grande"
                  onClick={() => setComprovante(true)}
                  disabled={!c}
                >
                  <Icone nome="file" tamanho={17} traco={1.6} />
                  Comprovante da semana
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {comprovante && c && d && (
        <Comprovante
          c={c}
          periodo={d.semana}
          pago={d.pagamento}
          aoFechar={() => setComprovante(false)}
        />
      )}
    </>
  );
}
